import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ApiResponseCode } from '@symphony-solo/shared/http/src';
import { ToastService } from '@symphony-solo/shared/toast/src';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { TermsAndConditionsService } from '../services/terms-and-conditions.service';
import {
  getFAQAction,
  getFAQErrorAction,
  getFAQSuccessAction,
  getPrivacyPolicyAction,
  getPrivacyPolicyErrorAction,
  getPrivacyPolicySuccessAction,
  getTermsAndConditionsAction,
  getTermsAndConditionsErrorAction,
  getTermsAndConditionsSuccessAction,
} from './terms-and-conditions.actions';

@Injectable()
export class TermsAndConditionsEffects {
  constructor(
    private action$: Actions,
    private termsAndConditionsService: TermsAndConditionsService,
    private navController: NavController,
    private toasterService: ToastService
  ) {}

  protected getTermsAndConditions$ = createEffect(() =>
    this.action$.pipe(
      ofType(getTermsAndConditionsAction),
      switchMap(() => {
        return this.termsAndConditionsService.getTermsAndConditions().pipe(
          map((termsAndConditions) => {
            return getTermsAndConditionsSuccessAction({
              termsAndConditions: termsAndConditions,
            });
          }),
          catchError(() => {
            const failures = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Fail to get terms and conditions', ApiResponseCode.Unknown_Error))
              .build();

            return of(getTermsAndConditionsErrorAction({ failures }));
          })
        );
      })
    )
  );

  protected getFAQ$ = createEffect(() =>
    this.action$.pipe(
      ofType(getFAQAction),
      switchMap(() => {
        return this.termsAndConditionsService.getFAQ().pipe(
          map((faq) => {
            return getFAQSuccessAction({
              faq,
            });
          }),
          catchError(() => {
            const failures = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Fail to get FAQs', ApiResponseCode.Unknown_Error))
              .build();

            return of(getFAQErrorAction({ failures }));
          })
        );
      })
    )
  );

  protected getPrivacyPolicy$ = createEffect(() =>
    this.action$.pipe(
      ofType(getPrivacyPolicyAction),
      switchMap(() => {
        return this.termsAndConditionsService.getPrivacyPolicy().pipe(
          map((privacyPolicy) => {
            return getPrivacyPolicySuccessAction({
              privacyPolicy,
            });
          }),
          catchError(() => {
            const failures = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Fail to get privacy policy', ApiResponseCode.Unknown_Error))
              .build();

            return of(getPrivacyPolicyErrorAction({ failures }));
          })
        );
      })
    )
  );

  protected geTermsAndConditionError$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(getPrivacyPolicyErrorAction, getFAQErrorAction, getTermsAndConditionsErrorAction),
        tap(() => {
          this.toasterService.showErrorToastOnTop('Oops something went wrong! Please try again!');
          this.navController.pop();
        })
      ),
    {
      dispatch: false,
    }
  );
}
